import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	IconButton,
	MenuItem,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { SetupChargeApiError, SetupChargeService } from "../../../../shared/services/api/setupCharge/SetupChargeService";
import { ISetupChargePartner, ISetupChargeWebhookDelivery } from "../../../../shared/services/api/setupCharge/Types";
import { copyToClipboard } from "../../../../shared/utils";
import { setupChargeFormatDate } from "../../utils/setupChargeFormat";

interface ModalSetupChargePartnerProps {
	onClose: () => void;
}

const NEW_PARTNER = "__new__";

const DELIVERY_STATE_LABEL: Record<string, { label: string; color: "success" | "warning" | "error" }> = {
	delivered: { label: "Entregue", color: "success" },
	pending: { label: "Pendente", color: "warning" },
	dead: { label: "Esgotou as tentativas", color: "error" },
};

/** Cadastro do parceiro integrado (ex.: 4SEG) e do destino dos webhooks. O segredo só aparece ao gerar. */
export const ModalSetupChargePartner = ({ onClose }: ModalSetupChargePartnerProps) => {
	const [partners, setPartners] = useState<ISetupChargePartner[]>([]);
	const [selected, setSelected] = useState<string>("");
	const [partnerKey, setPartnerKey] = useState("");
	const [name, setName] = useState("");
	const [webhookUrl, setWebhookUrl] = useState("");
	const [enabled, setEnabled] = useState(true);
	const [hasSecret, setHasSecret] = useState(false);
	const [updatedAt, setUpdatedAt] = useState<string | null>(null);
	const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
	const [deliveries, setDeliveries] = useState<ISetupChargeWebhookDelivery[]>([]);
	const [isBusy, setIsBusy] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [infoMessage, setInfoMessage] = useState<string | null>(null);

	const isNew = selected === NEW_PARTNER;

	const applyPartner = (partner: ISetupChargePartner) => {
		setPartnerKey(partner.partner);
		setName(partner.name ?? "");
		setWebhookUrl(partner.webhookUrl ?? "");
		setEnabled(partner.enabled ?? true);
		setHasSecret(!!partner.hasWebhookSecret);
		setUpdatedAt(partner.updatedAt ?? null);
	};

	const loadDeliveries = useCallback(async (key: string) => {
		const result = await SetupChargeService.getPartnerDeliveries(key);
		if (result instanceof Error) {
			setErrorMessage(result.message);
			return;
		}
		setDeliveries(result);
	}, []);

	const loadPartners = useCallback(async () => {
		setIsBusy(true);
		const result = await SetupChargeService.listPartners();
		setIsBusy(false);
		if (result instanceof Error) {
			setErrorMessage(result.message);
			return;
		}
		setPartners(result);
		if (result.length > 0) {
			setSelected(result[0].partner);
			applyPartner(result[0]);
			loadDeliveries(result[0].partner);
		} else {
			setSelected(NEW_PARTNER);
		}
	}, [loadDeliveries]);

	useEffect(() => {
		loadPartners();
	}, [loadPartners]);

	const handleSelect = (value: string) => {
		setSelected(value);
		setErrorMessage(null);
		setInfoMessage(null);
		setFieldErrors({});
		setRevealedSecret(null);
		setDeliveries([]);
		if (value === NEW_PARTNER) {
			setPartnerKey("");
			setName("");
			setWebhookUrl("");
			setEnabled(true);
			setHasSecret(false);
			setUpdatedAt(null);
			return;
		}
		const partner = partners.find((item) => item.partner === value);
		if (partner) applyPartner(partner);
		loadDeliveries(value);
	};

	const showError = (error: SetupChargeApiError) => {
		const byField: Record<string, string> = {};
		error.details.forEach((detail) => {
			if (detail.field) byField[detail.field] = detail.message ?? detail.reason ?? "";
		});
		setFieldErrors(byField);
		setErrorMessage(error.message);
	};

	const handleSave = async () => {
		setErrorMessage(null);
		setInfoMessage(null);
		const errors: Record<string, string> = {};
		if (!partnerKey.trim()) errors.partner = "Informe o identificador do parceiro (client_id do usuário do parceiro).";
		if (!name.trim()) errors.name = "Informe o nome do parceiro.";
		if (webhookUrl.trim() && !/^https?:\/\//i.test(webhookUrl.trim())) errors.webhookUrl = "A URL do webhook deve começar com https://.";
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;

		setIsBusy(true);
		const result = await SetupChargeService.savePartner(partnerKey.trim(), { name: name.trim(), webhookUrl: webhookUrl.trim() || null, enabled });
		setIsBusy(false);
		if (result instanceof Error) {
			showError(result as SetupChargeApiError);
			return;
		}
		setFieldErrors({});
		applyPartner(result);
		setSelected(result.partner);
		setPartners((current) => [...current.filter((item) => item.partner !== result.partner), result]);
		setInfoMessage("Parceiro salvo.");
		loadDeliveries(result.partner);
	};

	const handleRotate = async () => {
		setErrorMessage(null);
		setInfoMessage(null);
		setIsBusy(true);
		const result = await SetupChargeService.rotatePartnerSecret(partnerKey);
		setIsBusy(false);
		if (result instanceof Error) {
			showError(result as SetupChargeApiError);
			return;
		}
		setRevealedSecret(result.webhookSecret ?? null);
		setHasSecret(true);
		setPartners((current) => current.map((item) => (item.partner === result.partner ? { ...result, webhookSecret: undefined } : item)));
	};

	const handleTest = async () => {
		setErrorMessage(null);
		setInfoMessage(null);
		setIsBusy(true);
		const result = await SetupChargeService.testPartnerWebhook(partnerKey);
		setIsBusy(false);
		if (result instanceof Error) {
			showError(result as SetupChargeApiError);
			return;
		}
		setInfoMessage("Evento de teste enviado para a fila. Atualize a lista de entregas em instantes para ver o resultado.");
	};

	return (
		<Dialog open onClose={isBusy ? undefined : onClose} fullWidth maxWidth="md">
			<DialogTitle>Webhook do parceiro</DialogTitle>
			<DialogContent dividers>
				{errorMessage && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{errorMessage}
					</Alert>
				)}
				{infoMessage && (
					<Alert severity="success" sx={{ mb: 2 }}>
						{infoMessage}
					</Alert>
				)}

				<Grid container spacing={2}>
					<Grid item xs={12} sm={4}>
						<TextField select fullWidth size="small" label="Parceiro" value={selected} onChange={(e) => handleSelect(e.target.value)} disabled={isBusy && partners.length === 0}>
							{partners.map((partner) => (
								<MenuItem key={partner.partner} value={partner.partner}>
									{partner.name || partner.partner}
								</MenuItem>
							))}
							<MenuItem value={NEW_PARTNER}>Novo parceiro...</MenuItem>
						</TextField>
					</Grid>
					<Grid item xs={12} sm={8}>
						<TextField
							fullWidth
							size="small"
							label="Identificador do parceiro"
							value={partnerKey}
							onChange={(e) => setPartnerKey(e.target.value)}
							disabled={!isNew}
							error={!!fieldErrors.partner}
							helperText={fieldErrors.partner ?? (isNew ? "É o client_id do usuário que o parceiro usa para chamar a API." : undefined)}
						/>
					</Grid>
					<Grid item xs={12} sm={4}>
						<TextField fullWidth size="small" required label="Nome" value={name} onChange={(e) => setName(e.target.value)} error={!!fieldErrors.name} helperText={fieldErrors.name} />
					</Grid>
					<Grid item xs={12} sm={8}>
						<TextField
							fullWidth
							size="small"
							label="URL do webhook (https)"
							value={webhookUrl}
							onChange={(e) => setWebhookUrl(e.target.value)}
							error={!!fieldErrors.webhookUrl}
							helperText={fieldErrors.webhookUrl}
						/>
					</Grid>
					<Grid item xs={12}>
						<FormControlLabel control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />} label="Parceiro habilitado" />
						{updatedAt && (
							<Typography variant="caption" color="textSecondary" ml={2}>
								Última alteração em {setupChargeFormatDate(updatedAt, true)}
							</Typography>
						)}
					</Grid>
				</Grid>

				{!isNew && partnerKey && (
					<Box mt={2}>
						<Typography variant="subtitle2" fontWeight="bold" mb={1}>
							Segredo de assinatura
						</Typography>
						{revealedSecret ? (
							<Alert severity="warning" action={<Tooltip title="Copiar"><IconButton size="small" onClick={() => copyToClipboard(revealedSecret)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>}>
								Copie agora: este segredo não será exibido de novo.
								<Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all", mt: 0.5 }}>
									{revealedSecret}
								</Typography>
							</Alert>
						) : (
							<Typography variant="body2" color="textSecondary">
								{hasSecret ? "Segredo cadastrado (não pode ser consultado)." : "Nenhum segredo gerado: os webhooks saem sem assinatura."}
							</Typography>
						)}
						<Box display="flex" gap={1} mt={1}>
							<Button size="small" variant="outlined" onClick={handleRotate} disabled={isBusy}>
								{hasSecret ? "Rotacionar segredo" : "Gerar segredo"}
							</Button>
							<Button size="small" variant="outlined" onClick={handleTest} disabled={isBusy || !webhookUrl.trim()}>
								Enviar teste
							</Button>
						</Box>

						<Box display="flex" alignItems="center" justifyContent="space-between" mt={3} mb={1}>
							<Typography variant="subtitle2" fontWeight="bold">
								Últimas entregas
							</Typography>
							<Tooltip title="Atualizar">
								<IconButton size="small" onClick={() => loadDeliveries(partnerKey)}>
									<RefreshIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Box>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Data</TableCell>
									<TableCell>Evento</TableCell>
									<TableCell>Situação</TableCell>
									<TableCell>Tentativas</TableCell>
									<TableCell>Resposta / erro</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{deliveries.length === 0 && (
									<TableRow>
										<TableCell colSpan={5}>
											<Typography variant="body2" color="textSecondary" align="center">
												Nenhuma entrega registrada.
											</Typography>
										</TableCell>
									</TableRow>
								)}
								{deliveries.map((delivery) => {
									const state = DELIVERY_STATE_LABEL[delivery.state ?? ""];
									return (
										<TableRow key={delivery.id}>
											<TableCell>{setupChargeFormatDate(delivery.createdAt, true)}</TableCell>
											<TableCell>{delivery.eventType}</TableCell>
											<TableCell>{state ? <Chip size="small" label={state.label} color={state.color} /> : delivery.state}</TableCell>
											<TableCell>{delivery.attempts ?? 0}</TableCell>
											<TableCell>{delivery.lastError ?? (delivery.responseStatus ? `HTTP ${delivery.responseStatus}` : "")}</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isBusy}>
					Fechar
				</Button>
				<Button variant="contained" disableElevation onClick={handleSave} disabled={isBusy} startIcon={isBusy ? <CircularProgress size={16} /> : undefined}>
					Salvar parceiro
				</Button>
			</DialogActions>
		</Dialog>
	);
};
